import React, { useEffect, useState } from 'react';

import {
  Plan,
  PriceListGroup,
  PriceListGroupPeriodInput,
  PriceListGroupUpsert,
  PriceListPricingMethod,
  PricingHeader,
  Server,
} from '../types';

interface PriceListFormProps {
  initialData?: PriceListGroup | null;
  pricingHeaders: PricingHeader[];
  plans: Plan[];
  servers: Server[];
  onSubmit: (data: PriceListGroupUpsert) => void;
  onCancel: () => void;
}

interface FormPeriodRow {
  id: string;
  months: string;
  pricing_method: PriceListPricingMethod;
  monthly_price: string;
  relative_percentage: string;
}

interface FormState {
  name: string;
  pricing_header_id: number;
  plan_id: number;
  currency: string;
  country: string;
  server_ids: number[];
  product_type: 'dpms' | 'dsar' | 'wb' | 'ccms' | 'tp';
  valid_from: string;
  valid_to: string;
  is_active: boolean;
  periods: FormPeriodRow[];
}

interface PeriodFieldErrors {
  months?: string;
  monthly_price?: string;
  relative_percentage?: string;
}

interface FormErrors {
  name?: string;
  pricing_header_id?: string;
  plan_id?: string;
  currency?: string;
  valid_from?: string;
  valid_to?: string;
  country?: string;
  periods?: string;
  period_fields: Record<string, PeriodFieldErrors>;
}

const CURRENCIES = ['EUR', 'USD', 'GBP', 'BRL', 'CAD', 'CHF'] as const;
const SUPPORTED_MONTHS = [1, 3, 6, 12, 24] as const;

const createPeriodId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const createDefaultYearlyPeriod = (): FormPeriodRow => ({
  id: createPeriodId(),
  months: '12',
  pricing_method: 'absolute',
  monthly_price: '',
  relative_percentage: '',
});

const formatDateInput = (value?: string) => {
  if (!value) {
    return '';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }

  return parsed.toISOString().split('T')[0];
};

const parseNumericInput = (value: string): number | null => {
  if (value.trim() === '') {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const getInitialFormState = (initialData?: PriceListGroup | null): FormState => {
  if (!initialData) {
    return {
      name: '',
      pricing_header_id: 0,
      plan_id: 0,
      currency: 'EUR',
      country: '',
      server_ids: [],
      product_type: 'dpms',
      valid_from: new Date().toISOString().split('T')[0],
      valid_to: '',
      is_active: true,
      periods: [createDefaultYearlyPeriod()],
    };
  }

  const mappedPeriods: FormPeriodRow[] = initialData.periods
    .slice()
    .sort((left, right) => {
      if (left.months === 12 && right.months !== 12) {
        return -1;
      }
      if (right.months === 12 && left.months !== 12) {
        return 1;
      }
      return left.months - right.months;
    })
    .map((period) => ({
      id: createPeriodId(),
      months: String(period.months),
      pricing_method: 'absolute' as const,
      monthly_price:
        period.monthly_price !== null && period.monthly_price !== undefined
          ? String(period.monthly_price)
          : '',
      relative_percentage: '',
    }));

  const hasYearlyPeriod = mappedPeriods.some((period) => period.months === '12');
  if (!hasYearlyPeriod) {
    mappedPeriods.unshift(createDefaultYearlyPeriod());
  }

  return {
    name: initialData.name,
    pricing_header_id: initialData.pricing_header_id,
    plan_id: initialData.plan_id,
    currency: initialData.currency,
    country: initialData.country || '',
    server_ids:
      initialData.server_ids
      || initialData.servers.map((server) => server.id),
    product_type: initialData.product_type || 'dpms',
    valid_from: formatDateInput(initialData.valid_from),
    valid_to: formatDateInput(initialData.valid_to),
    is_active: initialData.is_active,
    periods: mappedPeriods,
  };
};

const PriceListForm: React.FC<PriceListFormProps> = ({
  initialData,
  pricingHeaders,
  plans,
  servers,
  onSubmit,
  onCancel,
}) => {
  const [formData, setFormData] = useState<FormState>(() => getInitialFormState(initialData));
  const [errors, setErrors] = useState<FormErrors>({ period_fields: {} });

  useEffect(() => {
    setFormData(getInitialFormState(initialData));
    setErrors({ period_fields: {} });
  }, [initialData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    let processedValue: string | number | boolean = value;
    if (type === 'checkbox') {
      processedValue = checked;
    } else if (name === 'pricing_header_id' || name === 'plan_id') {
      processedValue = parseInt(value, 10) || 0;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: processedValue,
    }));

    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => {
        const nextErrors = { ...prev };
        delete nextErrors[name as keyof FormErrors];
        return nextErrors;
      });
    }
  };

  const handlePeriodChange = (
    periodId: string,
    field: keyof Omit<FormPeriodRow, 'id'>,
    value: string,
  ) => {
    setFormData((prev) => ({
      ...prev,
      periods: prev.periods.map((period) =>
        period.id === periodId
          ? {
              ...period,
              [field]: value,
              ...(field === 'pricing_method' && value === 'absolute'
                ? { relative_percentage: '' }
                : {}),
              ...(field === 'pricing_method' && value === 'relative'
                ? { monthly_price: '' }
                : {}),
            }
          : period,
      ),
    }));

    setErrors((prev) => {
      const errorField =
        field === 'months' || field === 'monthly_price' || field === 'relative_percentage'
          ? field
          : null;

      if (!errorField || !prev.period_fields[periodId]) {
        return prev;
      }
      return {
        ...prev,
        period_fields: {
          ...prev.period_fields,
          [periodId]: {
            ...prev.period_fields[periodId],
            [errorField]: undefined,
          },
        },
      };
    });
  };

  const handleServerToggle = (serverId: number) => {
    setFormData((prev) => ({
      ...prev,
      server_ids: prev.server_ids.includes(serverId)
        ? prev.server_ids.filter((id) => id !== serverId)
        : [...prev.server_ids, serverId].sort((left, right) => left - right),
    }));
  };

  const handleAddPeriod = () => {
    setFormData((prev) => ({
      ...prev,
      periods: [
        ...prev.periods,
        {
          id: createPeriodId(),
          months: '',
          pricing_method: 'absolute',
          monthly_price: '',
          relative_percentage: '',
        },
      ],
    }));
  };

  const handleRemovePeriod = (periodId: string) => {
    setFormData((prev) => ({
      ...prev,
      periods: prev.periods.filter((period) => period.id !== periodId),
    }));
  };

  const getYearlyBaseline = () => {
    const yearlyPeriod = formData.periods.find((period) => period.months === '12');
    if (!yearlyPeriod) {
      return null;
    }
    return parseNumericInput(yearlyPeriod.monthly_price);
  };

  const getPreviewMonthlyPrice = (period: FormPeriodRow) => {
    if (period.pricing_method === 'absolute') {
      return parseNumericInput(period.monthly_price);
    }

    const yearlyBaseline = getYearlyBaseline();
    const relativePercentage = parseNumericInput(period.relative_percentage);
    if (yearlyBaseline === null || relativePercentage === null) {
      return null;
    }

    return yearlyBaseline * (1 + relativePercentage / 100);
  };

  const validate = (): boolean => {
    const nextErrors: FormErrors = {
      period_fields: {},
    };

    if (!formData.name.trim()) {
      nextErrors.name = 'Name is required';
    } else if (formData.name.length > 100) {
      nextErrors.name = 'Name must be less than 100 characters';
    }

    if (!formData.pricing_header_id) {
      nextErrors.pricing_header_id = 'Please select a pricing header';
    } else if (!pricingHeaders.find((header) => header.pricing_header_id === formData.pricing_header_id)) {
      nextErrors.pricing_header_id = 'Selected pricing header is invalid';
    }

    if (!formData.plan_id) {
      nextErrors.plan_id = 'Please select a plan';
    } else if (!plans.find((plan) => plan.plan_id === formData.plan_id)) {
      nextErrors.plan_id = 'Selected plan is invalid';
    }

    if (!formData.currency) {
      nextErrors.currency = 'Currency is required';
    }

    if (!formData.valid_from) {
      nextErrors.valid_from = 'Valid from date is required';
    }

    if (formData.valid_to) {
      const validFrom = new Date(formData.valid_from);
      const validTo = new Date(formData.valid_to);

      if (validTo <= validFrom) {
        nextErrors.valid_to = 'Valid to date must be after valid from date';
      }
    }

    if (formData.country && !/^[A-Z]{2}$/.test(formData.country.toUpperCase())) {
      nextErrors.country = 'Country must be a 2-letter ISO code (for example DE or US)';
    }

    const seenMonths = new Set<number>();
    let hasYearlyPeriod = false;

    // Allow empty periods for custom pricing scenarios
    // if (formData.periods.length === 0) {
    //   nextErrors.periods = 'At least the yearly contract period is required.';
    // }

    formData.periods.forEach((period) => {
      const periodErrors: PeriodFieldErrors = {};
      const months = parseNumericInput(period.months);

      if (months === null || !Number.isInteger(months) || months <= 0) {
        periodErrors.months = 'Enter a valid number of months.';
      } else {
        if (!SUPPORTED_MONTHS.includes(months as (typeof SUPPORTED_MONTHS)[number])) {
          periodErrors.months = 'Supported values are 1, 3, 6, 12, or 24 months.';
        }
        if (seenMonths.has(months)) {
          periodErrors.months = 'Each contract period can only be added once.';
        }
        seenMonths.add(months);
        if (months === 12) {
          hasYearlyPeriod = true;
        }
      }

      if (months === 12 || period.pricing_method === 'absolute') {
        const monthlyPrice = parseNumericInput(period.monthly_price);
        if (monthlyPrice === null) {
          periodErrors.monthly_price = 'Enter a monthly price.';
        }
      } else {
        const relativePercentage = parseNumericInput(period.relative_percentage);
        if (relativePercentage === null) {
          periodErrors.relative_percentage = 'Enter the percentage change from the yearly monthly price.';
        } else if (relativePercentage <= -100) {
          periodErrors.relative_percentage = 'Percentage must be greater than -100.';
        }
      }

      if (Object.keys(periodErrors).length > 0) {
        nextErrors.period_fields[period.id] = periodErrors;
      }
    });

    // Allow pricelists with no contract periods for custom pricing scenarios
    // if (!hasYearlyPeriod) {
    //   nextErrors.periods = 'The yearly contract period with 12 months is required.';
    // }

    setErrors(nextErrors);
    return Object.keys(nextErrors).filter((key) => key !== 'period_fields').length === 0
      && Object.keys(nextErrors.period_fields).length === 0;
  };

  const buildPayload = (): PriceListGroupUpsert => {
    const periods: PriceListGroupPeriodInput[] = formData.periods.map((period) => {
      const months = Number(period.months);
      if (months === 12 || period.pricing_method === 'absolute') {
        return {
          months,
          pricing_method: 'absolute',
          monthly_price: Number(period.monthly_price),
        };
      }

      return {
        months,
        pricing_method: 'relative',
        relative_percentage: Number(period.relative_percentage),
      };
    });

    const payload: PriceListGroupUpsert = {
      name: formData.name.trim(),
      pricing_header_id: formData.pricing_header_id,
      plan_id: formData.plan_id,
      currency: formData.currency,
      server_ids: [...formData.server_ids].sort((left, right) => left - right),
      product_type: formData.product_type,
      valid_from: new Date(formData.valid_from).toISOString(),
      is_active: formData.is_active,
      periods,
    };

    if (formData.country.trim()) {
      payload.country = formData.country.trim().toUpperCase();
    }

    if (formData.valid_to) {
      payload.valid_to = new Date(formData.valid_to).toISOString();
    }

    if (initialData?.billing_company_id !== undefined) {
      payload.billing_company_id = initialData.billing_company_id;
    }

    return payload;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) {
      return;
    }

    onSubmit(buildPayload());
  };

  const activePricingHeaders = pricingHeaders.filter((header) => header.is_active);
  const activePlans = plans.filter((plan) => plan.is_active);
  const sortedServers = servers
    .slice()
    .sort((left, right) => left.name.localeCompare(right.name, undefined, { sensitivity: 'base' }));

  return (
    <form onSubmit={handleSubmit} className="price-list-form">
      <div className="form-group">
        <label className="form-label">
          Name <span className="required">*</span>
        </label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          className={`form-control ${errors.name ? 'error' : ''}`}
          placeholder="Enter price list name"
          maxLength={100}
        />
        {errors.name && <div className="form-error">{errors.name}</div>}
      </div>

      <div className="form-row">
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">
              Pricing Header <span className="required">*</span>
            </label>
            <select
              name="pricing_header_id"
              value={formData.pricing_header_id}
              onChange={handleChange}
              className={`form-control ${errors.pricing_header_id ? 'error' : ''}`}
            >
              <option value={0}>-- Select a pricing header --</option>
              {activePricingHeaders.map((header) => (
                <option key={header.pricing_header_id} value={header.pricing_header_id}>
                  {header.name} {header.category ? `(${header.category})` : ''}
                </option>
              ))}
            </select>
            {errors.pricing_header_id && <div className="form-error">{errors.pricing_header_id}</div>}
          </div>
        </div>

        <div className="form-col">
          <div className="form-group">
            <label className="form-label">
              Plan <span className="required">*</span>
            </label>
            <select
              name="plan_id"
              value={formData.plan_id}
              onChange={handleChange}
              className={`form-control ${errors.plan_id ? 'error' : ''}`}
            >
              <option value={0}>-- Select a plan --</option>
              {activePlans.map((plan) => (
                <option key={plan.plan_id} value={plan.plan_id}>
                  {plan.name}
                </option>
              ))}
            </select>
            {errors.plan_id && <div className="form-error">{errors.plan_id}</div>}
          </div>
        </div>
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">
              Product Type <span className="required">*</span>
            </label>
            <select
              name="product_type"
              value={formData.product_type}
              onChange={handleChange}
              className="form-control"
            >
              <option value="dpms">DPMS</option>
              <option value="dsar">DSAR</option>
              <option value="wb">WB</option>
              <option value="ccms">CCMS</option>
              <option value="tp">TP</option>
            </select>
          </div>
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">
          Server Access <span className="optional">(optional)</span>
        </label>
        <div className="form-help" style={{ marginBottom: '12px' }}>
          Assign one or more servers to expose this pricelist to billing-token requests. Leave empty
          to keep it unavailable for server-authenticated pricing.
        </div>
        {sortedServers.length === 0 ? (
          <div className="form-help">No servers available.</div>
        ) : (
          <div
            className="card"
            style={{
              maxHeight: '220px',
              overflowY: 'auto',
              padding: '12px',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            }}
          >
            {sortedServers.map((server) => {
              const serverId = Number(server.id);
              if (!Number.isFinite(serverId)) {
                return null;
              }

              return (
                <label
                  key={server.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={formData.server_ids.includes(serverId)}
                    onChange={() => handleServerToggle(serverId)}
                  />
                  <span>
                    {server.name}
                    {!server.is_active ? ' (inactive)' : ''}
                  </span>
                </label>
              );
            })}
          </div>
        )}
        {formData.server_ids.length > 0 && (
          <div className="form-help" style={{ marginTop: '8px' }}>
            Restricted to {formData.server_ids.length} server{formData.server_ids.length === 1 ? '' : 's'}.
          </div>
        )}
      </div>

      <div className="form-row">
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">
              Currency <span className="required">*</span>
            </label>
            <select
              name="currency"
              value={formData.currency}
              onChange={handleChange}
              className={`form-control ${errors.currency ? 'error' : ''}`}
            >
              {CURRENCIES.map((currency) => (
                <option key={currency} value={currency}>
                  {currency}
                </option>
              ))}
            </select>
            {errors.currency && <div className="form-error">{errors.currency}</div>}
          </div>
        </div>

        <div className="form-col">
          <div className="form-group">
            <label className="form-label">
              Country <span className="optional">(optional)</span>
            </label>
            <input
              type="text"
              name="country"
              value={formData.country}
              onChange={handleChange}
              className={`form-control ${errors.country ? 'error' : ''}`}
              placeholder="for example DE or US"
              maxLength={2}
              style={{ textTransform: 'uppercase' }}
            />
            {errors.country && <div className="form-error">{errors.country}</div>}
          </div>
        </div>
      </div>

      <div className="form-row">
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">
              Valid From <span className="required">*</span>
            </label>
            <input
              type="date"
              name="valid_from"
              value={formData.valid_from}
              onChange={handleChange}
              className={`form-control ${errors.valid_from ? 'error' : ''}`}
            />
            {errors.valid_from && <div className="form-error">{errors.valid_from}</div>}
          </div>
        </div>

        <div className="form-col">
          <div className="form-group">
            <label className="form-label">
              Valid To <span className="optional">(optional)</span>
            </label>
            <input
              type="date"
              name="valid_to"
              value={formData.valid_to}
              onChange={handleChange}
              className={`form-control ${errors.valid_to ? 'error' : ''}`}
              min={formData.valid_from || undefined}
            />
            {errors.valid_to && <div className="form-error">{errors.valid_to}</div>}
          </div>
        </div>
      </div>

      <div className="form-group">
        <label className="checkbox-label">
          <input
            type="checkbox"
            name="is_active"
            checked={formData.is_active}
            onChange={handleChange}
            className="checkbox-input"
          />
          <span className="checkbox-text">Active</span>
        </label>
        <div className="form-help">
          The yearly contract is the default baseline. Additional periods derive their billing amount from the monthly yearly price.
          You can remove all periods to enable custom pricing without predefined contract terms.
        </div>
      </div>

      <div className="form-group">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
          <div>
            <label className="form-label" style={{ marginBottom: '4px' }}>
              Contract Periods
            </label>
            <div className="form-help">
              Supported contract periods: 1, 3, 6, 12, and 24 months.
            </div>
          </div>
          <button type="button" className="btn btn-secondary btn-sm" onClick={handleAddPeriod}>
            + Add Contract Period
          </button>
        </div>
        {errors.periods && <div className="form-error" style={{ marginTop: '8px' }}>{errors.periods}</div>}
      </div>

      {formData.periods.map((period) => {
        const months = parseNumericInput(period.months);
        const previewMonthlyPrice = getPreviewMonthlyPrice(period);
        const previewBilledAmount =
          previewMonthlyPrice !== null && months !== null
            ? previewMonthlyPrice * months
            : null;
        const isYearlyPeriod = period.months === '12';
        const periodErrors = errors.period_fields[period.id] || {};

        return (
          <div
            key={period.id}
            className="card"
            style={{ padding: '16px', marginBottom: '16px', border: isYearlyPeriod ? '1px solid #0d6efd' : undefined }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '12px',
                gap: '12px',
              }}
            >
              <div>
                <strong>{isYearlyPeriod ? 'Yearly Default Contract' : 'Additional Contract Period'}</strong>
                <div className="form-help">
                  {isYearlyPeriod
                    ? 'Set the monthly price for the 12-month contract.'
                    : 'Choose a contract period and either enter its monthly price directly or derive it from the yearly monthly price.'}
                </div>
              </div>
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={() => handleRemovePeriod(period.id)}
                title="Remove this contract period"
              >
                Remove
              </button>
            </div>

            <div className="form-row">
              <div className="form-col">
                <div className="form-group">
                  <label className="form-label">
                    Contract Period in Months <span className="required">*</span>
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="24"
                    step="1"
                    value={period.months}
                    onChange={(e) => handlePeriodChange(period.id, 'months', e.target.value)}
                    className={`form-control ${periodErrors.months ? 'error' : ''}`}
                    disabled={isYearlyPeriod}
                  />
                  {periodErrors.months && <div className="form-error">{periodErrors.months}</div>}
                </div>
              </div>

              <div className="form-col">
                <div className="form-group">
                  <label className="form-label">
                    Pricing Rule <span className="required">*</span>
                  </label>
                  <select
                    value={isYearlyPeriod ? 'absolute' : period.pricing_method}
                    onChange={(e) => handlePeriodChange(period.id, 'pricing_method', e.target.value)}
                    className="form-control"
                    disabled={isYearlyPeriod}
                  >
                    <option value="absolute">Set monthly price directly</option>
                    <option value="relative">Change yearly monthly price by %</option>
                  </select>
                </div>
              </div>
            </div>

            {isYearlyPeriod || period.pricing_method === 'absolute' ? (
              <div className="form-group">
                <label className="form-label">
                  Monthly Price <span className="required">*</span>
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={period.monthly_price}
                  onChange={(e) => handlePeriodChange(period.id, 'monthly_price', e.target.value)}
                  className={`form-control ${periodErrors.monthly_price ? 'error' : ''}`}
                  placeholder="0.00"
                />
                {periodErrors.monthly_price && <div className="form-error">{periodErrors.monthly_price}</div>}
              </div>
            ) : (
              <div className="form-group">
                <label className="form-label">
                  Percentage Change vs Yearly Monthly Price <span className="required">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={period.relative_percentage}
                  onChange={(e) => handlePeriodChange(period.id, 'relative_percentage', e.target.value)}
                  className={`form-control ${periodErrors.relative_percentage ? 'error' : ''}`}
                  placeholder="for example 10 for +10%"
                />
                {periodErrors.relative_percentage && (
                  <div className="form-error">{periodErrors.relative_percentage}</div>
                )}
              </div>
            )}

            <div className="form-help">
              {previewMonthlyPrice !== null && previewBilledAmount !== null
                ? `Preview: ${previewMonthlyPrice.toFixed(2)} ${formData.currency} per month, billed as ${previewBilledAmount.toFixed(2)} ${formData.currency} for ${months} month${months === 1 ? '' : 's'}.`
                : 'Preview appears once the pricing inputs are valid.'}
            </div>
          </div>
        );
      })}

      <div className="modal-footer">
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary">
          {initialData ? 'Update Price List' : 'Create Price List'}
        </button>
      </div>
    </form>
  );
};

export default PriceListForm;
